/**
 * useInAppBrowser — detects when the current page is loaded inside an embedded
 * WebView (Instagram, Facebook, Messenger, TikTok, LinkedIn etc.) where Google
 * blocks OAuth with `Error 403: disallowed_useragent`.
 *
 * Returns:
 *   {
 *     isInApp:  boolean,   // true → block Google sign-in, show blocker UI
 *     appName:  string,    // 'Instagram' | 'Facebook' | 'Messenger' | 'TikTok' | 'LinkedIn' | 'WebView' | ''
 *     os:       'ios' | 'android' | 'other',
 *     ua:       string,    // raw userAgent for diagnostics
 *   }
 */
import { useMemo } from 'react';

const detectInAppBrowser = () => {
  if (typeof navigator === 'undefined') return { isInApp: false, appName: '', os: 'other', ua: '' };
  const ua = navigator.userAgent || '';
  const lower = ua.toLowerCase();

  // OS
  let os = 'other';
  if (/iphone|ipad|ipod/i.test(ua)) os = 'ios';
  else if (/android/i.test(ua)) os = 'android';

  // Known in-app browser signatures
  // Refs: Google's disallowed_useragent enforcement + each app's published UA
  const signatures = [
    { name: 'Instagram', re: /Instagram/i },
    { name: 'Messenger', re: /\bMessenger\b|MessengerLite|FB_IAB\/MESSENGER/i },
    { name: 'Facebook',  re: /FBAN|FBAV|FB_IAB|FBIOS/i },
    { name: 'TikTok',    re: /TikTok|musical_ly|Bytedance|aweme/i },
    { name: 'LinkedIn',  re: /LinkedInApp/i },
    { name: 'Snapchat',  re: /Snapchat/i },
    { name: 'Twitter',   re: /Twitter|TwitterAndroid/i },
    { name: 'WeChat',    re: /MicroMessenger/i },
    { name: 'Line',      re: /\bLine\//i },
    { name: 'Pinterest', re: /Pinterest/i },
  ];

  for (const sig of signatures) {
    if (sig.re.test(ua)) {
      return { isInApp: true, appName: sig.name, os, ua };
    }
  }

  // Generic Android WebView fingerprint: "; wv)" appears in Chrome-based WebViews
  if (os === 'android' && /; wv\)/.test(ua)) {
    return { isInApp: true, appName: 'WebView', os, ua };
  }

  // iOS WebView heuristic: contains "AppleWebKit" + "Mobile" but lacks "Safari"
  if (os === 'ios'
      && /AppleWebKit/i.test(ua)
      && /Mobile/i.test(ua)
      && !/Safari\//i.test(ua)
      && !/CriOS\/|FxiOS\/|EdgiOS\//i.test(ua)) {
    return { isInApp: true, appName: 'WebView', os, ua };
  }

  return { isInApp: false, appName: '', os, ua: lower };
};

const useInAppBrowser = () => useMemo(() => detectInAppBrowser(), []);

export default useInAppBrowser;
export { detectInAppBrowser };
